export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

let seenPerson: string | null = null;

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(path, {
    credentials: "include",
    ...rest,
    headers: { ...(json !== undefined ? { "content-type": "application/json" } : {}), ...(seenPerson ? { "x-one-expect": seenPerson } : {}), ...(rest.headers ?? {}) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  // the browser's sign-in changed under this page (another account signed in elsewhere): never act on stale screens as someone else
  if (res.status === 409 && res.headers.get("content-type")?.includes("json")) {
    const b = await res.clone().json().catch(() => null);
    if (b?.error === "account_changed") { location.reload(); throw new ApiError(409, b.error, b.message); }
  }
  const who = res.headers.get("x-one-person");
  if (who) seenPerson = who;
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  // the shared sign-in cookie is gone (signed out elsewhere, or cleared): go to sign-in instead of showing errors on every card
  if (res.status === 401 && body?.error === "unauthenticated" && !location.pathname.startsWith("/login")) {
    location.assign(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
  }
  if (!res.ok) throw new ApiError(res.status, body?.error ?? "error", body?.message ?? res.statusText, body?.details);
  return body as T;
}

export const post = <T = unknown>(path: string, json: unknown) => api<T>(path, { method: "POST", json });
export const put = <T = unknown>(path: string, json: unknown) => api<T>(path, { method: "PUT", json });
export const patch = <T = unknown>(path: string, json: unknown) => api<T>(path, { method: "PATCH", json });
export const del = <T = unknown>(path: string, json?: unknown) => api<T>(path, { method: "DELETE", json: json ?? {} });

export interface Session {
  authenticated: boolean;
  access?: boolean;
  person?: { id: string; name: string; email: string | null; centralRole: string | null };
  grants?: Array<{ role: string; scopeType: string; scopeId: string | null }>;
  permissions?: string[];
}
